import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { RightOutlined } from '@ant-design/icons';
import './legal.css';

export type LegalNavigationItem = {
  id: string;
  label: string;
  icon: React.ReactNode;
};

export type LegalSummaryItem = {
  title: string;
  description: string;
  icon: React.ReactNode;
};

type LegalPageProps = {
  title: string;
  description: string;
  updatedAt: string;
  icon: React.ReactNode;
  navigation: readonly LegalNavigationItem[];
  summaryTitle: string;
  summaryItems: readonly LegalSummaryItem[];
  children: React.ReactNode;
};

const scrollToSection = (sectionId: string) => {
  document.getElementById(sectionId)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

const LegalPage: React.FC<LegalPageProps> = ({
  title,
  description,
  updatedAt,
  icon,
  navigation,
  summaryTitle,
  summaryItems,
  children,
}) => {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${title} | Jobs & Opportunities`;

    return () => {
      document.title = previousTitle;
    };
  }, [title]);

  return (
    <main className="legal-page">
      <section className="legal-hero" aria-labelledby="legal-page-title">
        <div className="public-content-rail legal-hero__inner">
          <div className="legal-hero__icon" aria-hidden="true">
            {icon}
          </div>
          <div>
            <nav className="legal-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <RightOutlined aria-hidden="true" />
              <span>Legal</span>
            </nav>
            <h1 id="legal-page-title">{title}</h1>
            <p className="legal-hero__description">{description}</p>
            <p className="legal-hero__updated">Last updated: {updatedAt}</p>
          </div>
        </div>
      </section>

      <div className="public-content-rail legal-layout">
        <aside className="legal-navigation">
          <nav aria-label={`${title} sections`}>
            <p className="legal-navigation__label">On this page</p>
            <ul>
              {navigation.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    onClick={() => scrollToSection(item.id)}
                  >
                    <span aria-hidden="true">{item.icon}</span>
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <article className="legal-content">
          <section
            className="legal-summary"
            aria-labelledby="legal-summary-title"
          >
            <h2 id="legal-summary-title">{summaryTitle}</h2>
            <div className="legal-summary__grid">
              {summaryItems.map((item) => (
                <div className="legal-summary__item" key={item.title}>
                  <span className="legal-summary__icon" aria-hidden="true">
                    {item.icon}
                  </span>
                  <div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
          {children}
        </article>
      </div>
    </main>
  );
};

export default LegalPage;
